import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { POST_URL } from '@utils/api/endpoints';
import { TranslateDocumentMutationParams } from '@utils/types/editors/translation';
import { QueryKeys } from '@utils/api/query-keys';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export function useTranslateDocument() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation(
    async ({
      sessionId,
      documentId,
      languages,
      filename,
      glossary_id
    }: TranslateDocumentMutationParams) => {
      const body: Record<string, unknown> = { languages, filename };
      if (glossary_id != null) {
        body.glossary_id = glossary_id;
      }

      const response = await Axios.post(
        POST_URL(sessionId, documentId).translateDocument,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );

      return { json: response.data.result };
    },
    {
      onSuccess: async (_, { sessionId, documentId }) => {
        // A completed translation can change credit for any saved document.
        await queryClient.invalidateQueries(['translationQuote']);
        await queryClient.invalidateQueries(
          QueryKeys.document(sessionId, documentId)
        );
        await queryClient.invalidateQueries(QueryKeys.folders());
        // #2294: the editor header's language picker reads from
        // `useGetDocumentTranslations`, and the split-view translation
        // list reads from `useGetSessionDocuments`. Without these two
        // invalidations the new translation only shows after a hard
        // refresh.
        await queryClient.invalidateQueries(
          QueryKeys.documentTranslations(sessionId, documentId)
        );
        await queryClient.invalidateQueries(['sessionDocuments', sessionId]);
        toast.success(t('document-translated-successfully'));
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        let messages: string[] = [];

        if (Array.isArray(error?.response?.data?.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messages = error.response.data.detail.map((err: any) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (error?.response?.data?.message) {
          messages = [error.response.data.message];
        } else {
          messages = [t('error-translating-document')];
        }

        toast.error(messages.join('\n'));
      },
      useErrorBoundary: false
    }
  );
}
